import type { OrderStatus } from "@bioflow/contracts";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminOrders } from "../../api/hooks";
import { NeedTelegram } from "../../components/NeedTelegram";
import { StatusBadge } from "../../components/OrderParts";
import { ORDER_STATUS_LABELS } from "../../content/checkout";
import { formatDateTime, formatMoney, formatOrderNumber } from "../../lib/money";
import { hasTelegramContext } from "../../lib/telegram";

const FILTERS: Array<OrderStatus | null> = [null, "new", "confirmed", "shipped", "delivered", "cancelled"];

/**
 * Очередь заказов для менеджера. Открывается по роли пользователя (/admin у
 * бота или пункт в профиле); без роли API отвечает 401, и экран показывает
 * это честно.
 */
export function AdminOrdersScreen() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<OrderStatus | null>("new");
  const orders = useAdminOrders(status);

  if (!hasTelegramContext()) return <NeedTelegram what="Админка" />;
  if (orders.isPending) return <div className="state">Загружаем заказы…</div>;
  if (orders.isError) return <div className="state">Нет доступа или API недоступен.</div>;

  return (
    <div className="screen screen--plain">
      <h1 className="screen__title">Заказы</h1>
      <p className="screen__subtitle">Свежие сверху. Откройте заказ, чтобы подтвердить, отправить или отменить.</p>

      <div className="chips">
        {FILTERS.map((filter) => (
          <button
            key={filter ?? "all"}
            type="button"
            className={`chip${status === filter ? " chip--active" : ""}`}
            onClick={() => setStatus(filter)}
          >
            {filter ? ORDER_STATUS_LABELS[filter] : "Все"}
          </button>
        ))}
      </div>

      {orders.data.items.length === 0 && <div className="state">В этом списке заказов нет</div>}

      {orders.data.items.map((order) => (
        <button
          key={order.orderNumber}
          type="button"
          className="row"
          onClick={() => navigate(`/admin/orders/${encodeURIComponent(order.orderNumber)}`)}
        >
          <div className="row__body">
            <div className="row__between">
              <div className="row__title">{formatOrderNumber(order.orderNumber)}</div>
              <StatusBadge status={order.status} />
            </div>
            <div className="row__meta">
              {order.contact.name} · {order.contact.phone}
              {order.contact.telegram ? ` · ${order.contact.telegram}` : ""}
            </div>
            <div className="row__between row__meta">
              <span>{formatDateTime(order.placedAt)} · {order.delivery.city}</span>
              <strong style={{ color: "var(--text)" }}>{formatMoney(order.total)}</strong>
            </div>
          </div>
          <ChevronRight size={18} strokeWidth={1.8} aria-hidden className="row__chevron" />
        </button>
      ))}
    </div>
  );
}
